import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, from, Observable, switchMap } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay } from 'rxjs/operators';
import { getFetched$ } from '../../dave-data-module/helper/helper';
import { State } from '../../dave-data-module/State';
import {
    getCommissionsActive,
    getCommissionsFetched,
} from '../../dave-data-module/State/selectors/commission.selector';
import { getMilestones, getMilestonesFetched } from '../../dave-data-module/State/selectors/milestone.selector';
import { FilterOption, FILTER_TYPE_SEARCH_MULTI_SELECT } from '../../dave-utils-module/app-filter-module/app-filter/app-filter.component';
import { getFilterSettings } from '../../helper/event.helper';
import { isNotNullOrUndefined } from '../../helper/helper';
import { CustomLabelService } from '../../services/custom-label/custom-label.service';
import { FilterTypes } from '../../services/default-filter.service';
import {
    getEmployeesActiveSortedByNameStartWithMe,
    getEmployeesFetched,
} from 'src/app/dave-data-module/State/selectors/employees.selectors';
import { selectEventCategories, selectEventCategoriesFetched } from '../../dave-data-module/State/selectors/event-Categories.selector';
import { EventCategoriesDataService } from '../../dave-data-module/services/event-categories.service-data-service';
import { getUser } from '../../dave-data-module/State/selectors/users.selectors';

type taskFilters = FilterTypes.Priority | FilterTypes.EndDate | FilterTypes.CommissionId | FilterTypes.UserId | FilterTypes.AutorId | FilterTypes.EventCategory;
type taskFiltersWithMilestone = taskFilters | FilterTypes.Milestones;

@Injectable({
    providedIn: 'root',
})
export class TaskFilterService {
    constructor(private store: Store<State>, private cls: CustomLabelService, private eventCategoriesDataService: EventCategoriesDataService,) {}

    categoriesWithAssignments$ = this.store.select(selectEventCategoriesFetched).pipe(
        switchMap(fetched =>
            fetched ? this.store.select(selectEventCategories) : from(this.eventCategoriesDataService.getRequest())
        )
    );

    filterSettings$: Observable<FilterOption<taskFilters>[]> = combineLatest([
        this.store.select(getUser).pipe(filter(u => !!u), switchMap(() => getFetched$(this.store, getEmployeesFetched, getEmployeesActiveSortedByNameStartWithMe))),
        getFetched$(this.store, getCommissionsFetched, getCommissionsActive),
        this.cls.getMultiple$('Commission'),
        this.categoriesWithAssignments$,
    ]).pipe(
        filter((v) => v.every(isNotNullOrUndefined)),
        map(([employees, commissions, commissionLabel, categoriesWithAssignments]) =>
            getFilterSettings<taskFilters>(
                [FilterTypes.Priority, FilterTypes.EndDate, FilterTypes.CommissionId, FilterTypes.UserId, FilterTypes.AutorId, FilterTypes.EventCategory],
                employees.filter((e) => e.UserId),
                employees.filter((e) => e.UserId),
                commissions,
                null,
                null,
                commissionLabel,
                categoriesWithAssignments,
            )
        ),
        shareReplay({ refCount: true, bufferSize: 1 }),
    );
    filterSettingsWithMilestones$ = (commissionId$: Observable<number | null>): Observable<FilterOption<taskFiltersWithMilestone | taskFilters>[]> => {
        return this.filterSettings$.pipe(
            switchMap((filterSettings) =>
                combineLatest([getFetched$(this.store, getMilestonesFetched, getMilestones), commissionId$.pipe(distinctUntilChanged())]).pipe(
                    map(([milestones, commissionId]) => {
                        if (!commissionId) {
                            return filterSettings;
                        } else {
                            const milestoneFilter = {
                                Name: FilterTypes.Milestones,
                                Type: FILTER_TYPE_SEARCH_MULTI_SELECT,
                                Label: 'Meilensteine',
                                // Icon: CommissionMeta.Icon,
                                Values: milestones
                                    .filter((m) => m.CommissionId === commissionId)
                                    .map((m) => ({
                                        label: m.Name,
                                        id: m.Id,
                                    })),
                            };
                            const commissionFilterIndex = filterSettings.findIndex((f) => f.Name === FilterTypes.CommissionId);
                            if (commissionFilterIndex > -1) {
                                return <FilterOption<taskFiltersWithMilestone>[]>[...filterSettings.slice(0, commissionFilterIndex + 1), milestoneFilter, ...filterSettings.slice(commissionFilterIndex + 1)];
                            }
                            return <FilterOption<taskFiltersWithMilestone>[]>[...filterSettings, milestoneFilter];
                        }
                    }),
                ),
            ),
            filter(isNotNullOrUndefined),
        );
    };
}
